// Data models for the regulations API (/regs/index.json, /regs/species/{id}.json, /regs/live,
// /regs/followables.json, /regs/changes.json, /regs/where.json).

export type Json = Record<string, any>;

export type Sector = 'recreational' | 'commercial';

export function sectorLabel(s: Sector): string {
  return s === 'recreational' ? 'Recreational' : 'Commercial';
}

export function otherSector(s: Sector): Sector {
  return s === 'recreational' ? 'commercial' : 'recreational';
}

/**
 * Whose rules: a jurisdiction key from the API ("nj", "ny", "federal", "federal-se"…).
 */
export class Waters {
  readonly key: string;

  constructor(key: string) {
    this.key = key;
  }

  static readonly nj = new Waters('nj');
  static readonly federal = new Waters('federal');
  static readonly values = [Waters.federal, Waters.nj];

  static of(id: string): Waters {
    switch (id) {
      case 'federal-ne':
      case 'federal':
        return Waters.federal;
      default:
        return new Waters(id);
    }
  }

  get isFederal(): boolean {
    return this.key === 'federal' || this.key.startsWith('federal-');
  }

  get keys(): string[] {
    return this.key === 'federal' ? ['federal', 'federal-ne'] : [this.key];
  }

  get label(): string {
    switch (this.key) {
      case 'federal':
        return 'Federal waters';
      case 'federal-se':
        return 'Federal waters (Southeast)';
      case 'federal-gulf':
        return 'Federal waters (Gulf)';
      case 'fl':
        return 'FL state waters (Atlantic)';
      case 'fl-gulf':
        return 'FL state waters (Gulf)';
      default:
        return this.isFederal ? 'Federal waters' : `${this.key.toUpperCase()} state waters`;
    }
  }

  get lowerLabel(): string {
    return this.isFederal ? this.label.replace('Federal', 'federal') : this.label;
  }

  equals(other: Waters | null | undefined): boolean {
    return other != null && other.key === this.key;
  }
}

export function atWaters(m: Record<string, any> | null | undefined, w: Waters): any {
  if (!m) return null;
  for (const k of w.keys) {
    if (k in m) return m[k];
  }
  return null;
}

export function shortName(name: string): string {
  const n = name.replace(/^Atlantic\s+/i, '');
  return n.length === 0 ? n : n[0].toUpperCase() + n.substring(1);
}

export interface Quick {
  bag: string;
  min: string;
  max: string;
}

export function parseQuick(j?: Json | null): Quick {
  return {
    bag: j?.['bag'] != null ? String(j['bag']) : '',
    min: j?.['min'] != null ? String(j['min']) : '',
    max: j?.['max'] != null ? String(j['max']) : '',
  };
}

export interface Season {
  from: string; // "MM-DD"
  to: string; // "MM-DD"
  label: string | null;
  closed: boolean;
}

export function parseSeason(j: Json): Season {
  return {
    from: String(j['from'] ?? ''),
    to: String(j['to'] ?? ''),
    label: j['label'] != null ? String(j['label']) : null,
    closed: j['closed'] === true,
  };
}

export interface NoRetentionDays {
  from: string;
  to: string;
  label: string;
  weekdays: string[]; // "Mon".."Sun"
}

export function parseNoRetentionDays(j: Json): NoRetentionDays {
  return {
    from: String(j['from'] ?? ''),
    to: String(j['to'] ?? ''),
    label: String(j['label'] ?? ''),
    weekdays: Array.isArray(j['weekdays']) ? j['weekdays'].map(String) : [],
  };
}

export interface TodayStatus {
  status: string; // open | closed | prohibited | catch_and_release | not_applicable
  current: string[];
  closures: string[];
  nextFrom: string | null;
  nextLabel: string | null;
  noRetention: string | null;
}

export function parseTodayStatus(j?: Json | null): TodayStatus {
  const next = j?.['next'];
  return {
    status: j?.['status'] != null ? String(j['status']) : 'not_applicable',
    current: Array.isArray(j?.['current']) ? j!['current'].map(String) : [],
    closures: Array.isArray(j?.['closures']) ? j!['closures'].map(String) : [],
    nextFrom: next && typeof next === 'object' && next['from'] != null ? String(next['from']) : null,
    nextLabel: next && typeof next === 'object' && next['label'] != null ? String(next['label']) : null,
    noRetention: j?.['no_retention'] != null ? String(j['no_retention']) : null,
  };
}

export interface SourceLink {
  title: string;
  url: string;
}

export function parseSourceLink(j?: Json | null): SourceLink {
  return {
    title: j?.['title'] != null ? String(j['title']) : 'Official source',
    url: j?.['url'] != null ? String(j['url']) : '',
  };
}

export class Rule {
  readonly status: string;
  readonly seasons: Season[];
  readonly bag: string | null;
  readonly minSize: string | null;
  readonly maxSize: string | null;
  readonly slot: string | null;
  readonly vesselLimit: string | null;
  readonly permit: string | null;
  readonly gear: string | null;
  readonly notes: string[];
  readonly source: SourceLink;
  readonly alsoSee: SourceLink[];
  readonly confidence: string | null;
  readonly quick: Quick;
  readonly noRetentionDays: NoRetentionDays[];
  readonly today: TodayStatus;
  private readonly _areaRules: Record<string, any>;

  constructor(j: Json) {
    this.status = String(j['status'] ?? '');
    this.seasons = Array.isArray(j['seasons']) ? j['seasons'].map(parseSeason) : [];
    this.bag = j['bag'] != null ? String(j['bag']) : null;
    this.minSize = j['min_size'] != null ? String(j['min_size']) : null;
    this.maxSize = j['max_size'] != null ? String(j['max_size']) : null;
    this.slot = j['slot'] != null ? String(j['slot']) : null;
    this.vesselLimit = j['vessel_limit'] != null ? String(j['vessel_limit']) : null;
    this.permit = j['permit'] != null ? String(j['permit']) : null;
    this.gear = j['gear'] != null ? String(j['gear']) : null;
    this.confidence = j['confidence'] != null ? String(j['confidence']) : null;
    this.notes = Array.isArray(j['notes']) ? j['notes'].map(String) : [];
    this.source = parseSourceLink(j['source']);
    this.alsoSee = Array.isArray(j['also_see']) ? j['also_see'].map(parseSourceLink) : [];
    this.quick = parseQuick(j['quick']);
    this.noRetentionDays = Array.isArray(j['no_retention_days']) ? j['no_retention_days'].map(parseNoRetentionDays) : [];
    this.today = parseTodayStatus(j['today']);
    this._areaRules = (j['area_rules'] && typeof j['area_rules'] === 'object') ? j['area_rules'] : {};
  }

  areaRule(area: string | null | undefined): Rule | null {
    if (!area) return null;
    const r = this._areaRules[area];
    return r && typeof r === 'object' ? new Rule(r) : null;
  }
}

export interface Lookalike {
  name: string;
  tell: string;
}

export function parseLookalike(j: Json): Lookalike {
  return {
    name: String(j['name'] ?? ''),
    tell: String(j['tell'] ?? ''),
  };
}

export class SpeciesSummary {
  readonly id: string;
  readonly name: string;
  readonly group: string;
  readonly aka: string[];
  readonly live: string[];
  readonly availableIn: string[];
  private readonly _today: Record<string, any>;
  private readonly _quick: Record<string, any>;
  private readonly _noRet: Record<string, any>;
  private readonly _current: Record<string, any>;
  private readonly _next: Record<string, any>;

  constructor(j: Json) {
    this.id = String(j['id'] ?? '');
    this.name = String(j['name'] ?? '');
    this.group = String(j['group'] ?? '');
    this.aka = Array.isArray(j['aka']) ? j['aka'].map(String) : [];
    this.live = Array.isArray(j['live']) ? j['live'].map(String) : [];
    this.availableIn = Array.isArray(j['available_in']) ? j['available_in'].map(String) : [];
    this._today = (j['today'] && typeof j['today'] === 'object') ? j['today'] : {};
    this._quick = (j['quick'] && typeof j['quick'] === 'object') ? j['quick'] : {};
    this._noRet = (j['no_retention'] && typeof j['no_retention'] === 'object') ? j['no_retention'] : {};
    this._current = (j['current'] && typeof j['current'] === 'object') ? j['current'] : {};
    this._next = (j['next'] && typeof j['next'] === 'object') ? j['next'] : {};
  }

  get short(): string {
    return shortName(this.name);
  }

  private isAvailableIn(w: Waters): boolean {
    return this.availableIn.length === 0 || w.keys.some((k) => this.availableIn.includes(k));
  }

  hasRules(w: Waters): boolean {
    if (!this.isAvailableIn(w)) return false;
    return Object.values(this._today).some((m) => m && typeof m === 'object' && atWaters(m, w) != null);
  }

  hasRulesFor(s: Sector, w: Waters): boolean {
    if (!this.isAvailableIn(w)) return false;
    const m = this._today[s];
    return m && typeof m === 'object' && atWaters(m, w) != null;
  }

  status(s: Sector, w: Waters): string {
    const val = atWaters(this._today[s], w);
    return val != null ? String(val) : 'not_applicable';
  }

  quick(s: Sector, w: Waters): Quick {
    return parseQuick(atWaters(this._quick[s], w));
  }

  noRetention(s: Sector, w: Waters): string | null {
    const val = atWaters(this._noRet[s], w);
    return val != null ? String(val) : null;
  }

  current(s: Sector, w: Waters): string[] {
    const val = atWaters(this._current[s], w);
    return Array.isArray(val) ? val.map(String) : [];
  }

  next(s: Sector, w: Waters): { from: string; label: string | null } | null {
    const n = atWaters(this._next[s], w);
    if (!n || typeof n !== 'object' || n['from'] == null) return null;
    return {
      from: String(n['from']),
      label: n['label'] != null ? String(n['label']) : null,
    };
  }
}

export const COAST_ORDER: readonly string[] = [
  'me', 'nh', 'ma', 'ri', 'ct', 'ny', 'nj', 'pa', 'de', 'md', 'va',
  'nc', 'sc', 'ga', 'fl', 'fl-gulf', 'al', 'ms', 'la', 'tx',
  'federal', 'federal-se', 'federal-gulf',
];

export class RegsIndex {
  readonly version: string;
  readonly verified: string;
  readonly disclaimer: string;
  readonly groups: string[];
  readonly species: SpeciesSummary[];
  readonly savedCopyFrom: Date | null;
  private readonly _jurisdictions: Record<string, any>;

  constructor(j: Json) {
    this.version = String(j['version'] ?? '');
    this.verified = String(j['verified'] ?? '');
    this.disclaimer = String(j['disclaimer'] ?? '');
    this.groups = Array.isArray(j['groups']) ? j['groups'].map(String) : [];
    this.species = Array.isArray(j['species']) ? j['species'].map((s: Json) => new SpeciesSummary(s)) : [];
    this._jurisdictions = (j['jurisdictions'] && typeof j['jurisdictions'] === 'object') ? j['jurisdictions'] : {};
    this.savedCopyFrom = j['_savedCopyFrom'] ? new Date(j['_savedCopyFrom']) : null;
  }

  covers(w: Waters): boolean {
    if (Object.keys(this._jurisdictions).length === 0) {
      return Waters.values.some((v) => v.equals(w));
    }
    const val = atWaters(this._jurisdictions, w);
    if (!val || typeof val !== 'object') return false;
    return 'covered' in val ? val['covered'] === true : true;
  }

  hasRules(w: Waters): boolean {
    return this.species.some((s) => s.hasRules(w));
  }

  available(w: Waters): boolean {
    return this.covers(w) || this.hasRules(w);
  }

  get allWaters(): Waters[] {
    const keys = new Set<string>();
    for (const k of Object.keys(this._jurisdictions)) keys.add(k);
    for (const sp of this.species) {
      for (const s of ['recreational', 'commercial'] as Sector[]) {
        const m = (sp as any)._today?.[s];
        if (m && typeof m === 'object') {
          for (const k of Object.keys(m)) keys.add(k);
        }
      }
    }
    const list: Waters[] = [];
    const seen = new Set<string>();
    const candidates = [
      ...Array.from(keys).map((k) => Waters.of(k)),
      ...(Object.keys(this._jurisdictions).length === 0 ? Waters.values : []),
    ];

    for (const w of candidates) {
      if (!seen.has(w.key) && this.available(w)) {
        seen.add(w.key);
        list.push(w);
      }
    }

    const rank = (w: Waters): number => {
      const idx = COAST_ORDER.indexOf(w.key);
      return idx >= 0 ? idx : COAST_ORDER.length;
    };

    list.sort((a, b) => {
      const rA = rank(a);
      const rB = rank(b);
      if (rA !== rB) return rA - rB;
      return a.label.localeCompare(b.label);
    });

    return list;
  }
}

export class Species {
  readonly id: string;
  readonly name: string;
  readonly scientific: string;
  readonly group: string;
  readonly disclaimer: string;
  readonly verified: string | null;
  readonly todayMd: string;
  readonly savedCopyFrom: Date | null;
  readonly aka: string[];
  readonly idNotes: string[];
  readonly liveSectors: string[];
  readonly lookalikes: Lookalike[];
  private readonly _rules: Record<string, any>;
  private readonly _jurisdictions: Record<string, any>;

  constructor(j: Json) {
    this.id = String(j['id'] ?? '');
    this.name = String(j['name'] ?? '');
    this.scientific = String(j['scientific'] ?? '');
    this.group = String(j['group'] ?? '');
    this.disclaimer = String(j['disclaimer'] ?? '');
    this.verified = j['verified'] != null ? String(j['verified']) : null;
    this.todayMd = String(j['today'] ?? '');
    this.savedCopyFrom = j['_savedCopyFrom'] ? new Date(j['_savedCopyFrom']) : null;
    this.aka = Array.isArray(j['aka']) ? j['aka'].map(String) : [];
    this.idNotes = Array.isArray(j['id_notes']) ? j['id_notes'].map(String) : [];
    this.lookalikes = Array.isArray(j['lookalikes']) ? j['lookalikes'].map(parseLookalike) : [];
    this.liveSectors = (j['live'] && typeof j['live'] === 'object') ? Object.keys(j['live']) : [];
    this._rules = (j['rules'] && typeof j['rules'] === 'object') ? j['rules'] : {};
    this._jurisdictions = (j['jurisdictions'] && typeof j['jurisdictions'] === 'object') ? j['jurisdictions'] : {};
  }

  get short(): string {
    return shortName(this.name);
  }

  get realIdNotes(): string[] {
    return this.idNotes.filter((n) => !n.startsWith('Identifying characteristics for'));
  }

  watersNote(w: Waters): string | null {
    const jur = atWaters(this._jurisdictions, w);
    const n = jur?.['note'] != null ? String(jur['note']).trim() : '';
    return n.length > 0 ? n : null;
  }

  rule(s: Sector, w: Waters): Rule | null {
    const r = atWaters(this._rules[s], w);
    return r && typeof r === 'object' ? new Rule(r) : null;
  }

  hasLive(s: Sector): boolean {
    return this.liveSectors.includes(s);
  }
}

export class LiveRow {
  readonly label: string;
  readonly status: string | null;
  readonly quota: number | null;
  readonly landed: number | null;
  readonly pct: number | null;
  readonly remaining: number | null;
  readonly unit: string;

  constructor(j: Json) {
    this.label = String(j['label'] ?? '');
    const st = j['status'] != null ? String(j['status']).trim() : '';
    this.status = st.length > 0 ? st : null;
    this.quota = parseNumber(j['quota']);
    this.landed = parseNumber(j['landed']);
    this.pct = parseNumber(j['pct']);
    this.remaining = parseNumber(j['remaining']);
    this.unit = String(j['unit'] ?? '');
  }

  get isQuota(): boolean {
    return this.quota != null && (this.unit === 'lb' || this.unit === 'mt');
  }
}

function parseNumber(v: any): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const num = parseFloat(v.replace(/,/g, ''));
    return isNaN(num) ? null : num;
  }
  return null;
}

export class LiveSource {
  readonly name: string;
  readonly url: string;
  readonly error: string | null;
  readonly dataThrough: string | null;
  readonly rows: LiveRow[];

  constructor(j: Json) {
    this.name = String(j['name'] ?? '');
    this.url = String(j['url'] ?? '');
    this.error = j['error'] != null ? String(j['error']) : null;
    const meta = j['meta'];
    this.dataThrough = (meta && typeof meta === 'object')
      ? (meta['data_through'] != null ? String(meta['data_through']) : (meta['page_updated'] != null ? String(meta['page_updated']) : null))
      : null;
    this.rows = Array.isArray(j['summary']) ? j['summary'].map((r: Json) => new LiveRow(r)) : [];
  }
}

export class LiveData {
  readonly fetched: Date | null;
  readonly sources: LiveSource[];
  private readonly _rows: LiveRow[];

  constructor(j: Json) {
    this.fetched = j['fetched'] ? new Date(j['fetched']) : null;
    this.sources = Array.isArray(j['sources']) ? j['sources'].map((s: Json) => new LiveSource(s)) : [];
    this._rows = Array.isArray(j['rows']) ? j['rows'].map((r: Json) => new LiveRow(r)) : [];
  }

  get rows(): LiveRow[] {
    const fromSources: LiveRow[] = [];
    for (const s of this.sources) fromSources.push(...s.rows);
    return fromSources.length > 0 ? fromSources : this._rows;
  }

  get hasAnything(): boolean {
    return this.rows.some((r) => r.quota != null || r.status != null);
  }

  get hasQuotaNumbers(): boolean {
    return this.rows.some((r) => r.quota != null && r.landed != null && (r.unit === 'lb' || r.unit === 'mt'));
  }
}

export class Followable {
  readonly id: string;
  readonly species: string;
  readonly name: string;
  readonly sector: Sector;
  readonly kinds: string[];
  readonly topics: Record<string, string>;

  constructor(j: Json) {
    this.id = String(j['id'] ?? '');
    this.species = String(j['species'] ?? '');
    this.name = String(j['name'] ?? '');
    this.sector = j['sector'] === 'commercial' ? 'commercial' : 'recreational';
    this.kinds = Array.isArray(j['kinds']) ? j['kinds'].map(String) : [];
    this.topics = {};
    if (j['topics'] && typeof j['topics'] === 'object') {
      for (const [k, v] of Object.entries(j['topics'])) {
        this.topics[k] = String(v);
      }
    }
  }

  get short(): string {
    return shortName(this.name);
  }

  static idFor(speciesId: string, s: Sector): string {
    return `${speciesId}.${s}`;
  }

  static topicFor(speciesId: string, s: Sector, kind: string): string {
    return `regs.${speciesId}.${s}.${kind}`;
  }
}

function extractFollowId(j: Json): string {
  const f = String(j['follow'] ?? '');
  const match = f.match(/^regs\.([a-z0-9-]+)\.(recreational|commercial)(\.[a-z]+)?$/);
  if (match) return `${match[1]}.${match[2]}`;
  if (f.length === 0 && j['species'] != null) {
    return `${j['species']}.${j['sector']}`;
  }
  return f;
}

export class ChangeEvent {
  readonly id: string;
  readonly at: Date;
  readonly follow: string;
  readonly species: string;
  readonly name: string;
  readonly sector: Sector;
  readonly kind: string;
  readonly title: string;
  readonly headline: string;
  readonly text: string;
  readonly jurisdictions: string[];
  readonly data: Json;

  constructor(j: Json) {
    this.id = String(j['id'] ?? '');
    this.at = j['at'] ? new Date(j['at']) : new Date();
    this.follow = extractFollowId(j);
    this.species = String(j['species'] ?? '');
    this.name = String(j['name'] ?? '');
    this.sector = j['sector'] === 'commercial' ? 'commercial' : 'recreational';
    this.kind = String(j['kind'] ?? '');
    this.title = String(j['title'] ?? '');
    this.headline = String(j['headline'] ?? '');
    this.text = String(j['text'] ?? '');
    this.jurisdictions = Array.isArray(j['jurisdictions']) ? j['jurisdictions'].map(String) : [];
    this.data = (j['data'] && typeof j['data'] === 'object') ? j['data'] : {};
  }

  get short(): string {
    return shortName(this.name);
  }

  get rows(): Json[] {
    return Array.isArray(this.data['rows']) ? this.data['rows'] : [];
  }
}

export enum RegsArea {
  state = 'state',
  federal = 'federal',
  notCovered = 'notCovered',
  outside = 'outside',
}

export class RegsWhere {
  readonly area: RegsArea;
  readonly jurisdiction: string | null;
  readonly water: string | null;
  readonly offshoreNm: number | null;
  readonly name: string | null;
  readonly state: string | null;
  readonly nearestState: string | null;
  readonly federalRegion: string | null;
  readonly nearBoundary: boolean;
  readonly onLand: boolean;
  readonly fromServer: boolean;
  readonly extraNote: string | null;

  constructor(
    area: RegsArea,
    opts: {
      jurisdiction?: string | null;
      water?: string | null;
      offshoreNm?: number | null;
      name?: string | null;
      state?: string | null;
      nearestState?: string | null;
      federalRegion?: string | null;
      nearBoundary?: boolean;
      onLand?: boolean;
      fromServer?: boolean;
      extraNote?: string | null;
    } = {}
  ) {
    this.area = area;
    this.jurisdiction = opts.jurisdiction ?? null;
    this.water = opts.water ?? null;
    this.offshoreNm = opts.offshoreNm ?? null;
    this.name = opts.name ?? null;
    this.state = opts.state ?? null;
    this.nearestState = opts.nearestState ?? null;
    this.federalRegion = opts.federalRegion ?? null;
    this.nearBoundary = opts.nearBoundary ?? false;
    this.onLand = opts.onLand ?? false;
    this.fromServer = opts.fromServer ?? false;
    this.extraNote = opts.extraNote ?? null;
  }

  static fromServer(j: Json): RegsWhere {
    const kind = String(j['kind'] ?? '');
    const jur = j['jurisdiction'] != null ? String(j['jurisdiction']) : null;
    const covered = j['covered'] === true;
    let area: RegsArea;
    if (kind === 'state' && jur && covered) {
      area = RegsArea.state;
    } else if (kind === 'federal' && jur && covered) {
      area = RegsArea.federal;
    } else if (kind === 'state' || kind === 'federal') {
      area = RegsArea.notCovered;
    } else {
      area = RegsArea.outside;
    }

    return new RegsWhere(area, {
      jurisdiction: jur,
      water: j['area'] != null ? String(j['area']) : null,
      offshoreNm: parseNumber(j['offshore_nm']),
      name: j['name'] != null ? String(j['name']) : null,
      state: j['state'] != null ? String(j['state']) : null,
      nearestState: j['nearest_state'] != null ? String(j['nearest_state']) : null,
      federalRegion: j['federal_region'] != null ? String(j['federal_region']) : null,
      nearBoundary: j['near_boundary'] === true,
      onLand: j['on_land'] === true,
      fromServer: true,
      extraNote: j['note'] != null ? String(j['note']) : null,
    });
  }

  get inBay(): boolean {
    return this.water != null && this.water !== 'open-coast';
  }

  get waterName(): string | null {
    if (!this.water) return null;
    return this.water
      .split('-')
      .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.substring(1) : w))
      .join(' ')
      .replace(/^Lower Ny /, 'Lower NY ');
  }

  get waters(): Waters | null {
    if (!this.jurisdiction) return null;
    return Waters.of(this.jurisdiction);
  }

  get place(): Waters | null {
    if (this.jurisdiction) return Waters.of(this.jurisdiction);
    if (this.state) return new Waters(this.state);
    if (this.federalRegion) return Waters.of(this.federalRegion);
    return null;
  }

  pickable(available: (w: Waters) => boolean): Waters[] {
    const out: Waters[] = [];
    const seen = new Set<string>();
    const add = (k?: string | null) => {
      if (!k) return;
      const w = Waters.of(k);
      if (!seen.has(w.key) && available(w)) {
        seen.add(w.key);
        out.push(w);
      }
    };
    add(this.state);
    add(this.jurisdiction);
    add(this.nearestState);
    add(this.federalRegion ?? 'federal');
    return out;
  }
}

export function regsPair(current: Waters, here?: RegsWhere | null): Waters | null {
  if (current.isFederal) {
    const st = here?.state ?? here?.nearestState ?? 'nj';
    return new Waters(st);
  }
  return Waters.of(here?.federalRegion ?? 'federal');
}

export const STATE_NAMES: Record<string, string> = {
  me: 'Maine',
  nh: 'New Hampshire',
  ma: 'Massachusetts',
  ri: 'Rhode Island',
  ct: 'Connecticut',
  ny: 'New York',
  nj: 'New Jersey',
  pa: 'Pennsylvania',
  de: 'Delaware',
  md: 'Maryland',
  va: 'Virginia',
  nc: 'North Carolina',
  sc: 'South Carolina',
  ga: 'Georgia',
  fl: 'Florida',
  'fl-gulf': 'Florida (Gulf)',
  al: 'Alabama',
  ms: 'Mississippi',
  la: 'Louisiana',
  tx: 'Texas',
};

export function regsStateName(code?: string | null): string {
  if (!code) return '';
  return STATE_NAMES[code.toLowerCase()] || code.toUpperCase();
}

export const regsConfirmText =
  'Regulations change; confirm with the official source before you keep a fish.';

